import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, BarChart3, Shield, Globe, Info, Sparkles, Coins, TrendingUp, Wallet, ChartLine } from 'lucide-react';
import { Button } from '../ui/button';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';

const HeroSection = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { t, isRTL } = useLanguage();

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
        delayChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 100,
        damping: 15
      }
    }
  };

  const floatingVariants = {
    initial: { y: 0 },
    animate: {
      y: [-10, 10, -10],
      transition: {
        duration: 6,
        repeat: Infinity,
        ease: "easeInOut"
      }
    }
  };

  return (
    <section className="relative min-h-screen flex items-center pt-20 md:pt-24 pb-16 overflow-hidden bg-[#0a0a0f]">
      {/* Animated Background */}
      <div className="absolute inset-0">
        {/* Gradient Orbs with motion */}
        <motion.div 
          className="absolute top-1/4 left-1/4 w-72 md:w-96 h-72 md:h-96 bg-[#D4AF37]/10 rounded-full blur-3xl"
          animate={{
            scale: [1, 1.2, 1],
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            ease: "easeInOut"
          }}
        />
        <motion.div 
          className="absolute bottom-1/4 right-1/4 w-64 md:w-80 h-64 md:h-80 bg-[#D4AF37]/5 rounded-full blur-3xl"
          animate={{
            scale: [1.2, 1, 1.2],
            opacity: [0.2, 0.4, 0.2],
          }}
          transition={{
            duration: 10,
            repeat: Infinity,
            ease: "easeInOut",
            delay: 1
          }}
        />
        
        {/* Grid Pattern */}
        <div 
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(rgba(212, 175, 55, 0.3) 1px, transparent 1px),
                              linear-gradient(90deg, rgba(212, 175, 55, 0.3) 1px, transparent 1px)`,
            backgroundSize: '50px 50px'
          }}
        />

        {/* Floating particles */}
        {[...Array(6)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-[#D4AF37]/40 rounded-full hidden md:block"
            style={{
              left: `${15 + i * 15}%`,
              top: `${20 + (i % 3) * 25}%`,
            }}
            animate={{
              y: [0, -30, 0],
              opacity: [0.2, 0.6, 0.2],
            }}
            transition={{
              duration: 3 + i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
              delay: i * 0.3
            }}
          />
        ))}
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <motion.div 
          className="max-w-3xl"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Badge */}
          <motion.div 
            className="inline-flex items-center gap-2 px-4 py-2 bg-[#D4AF37]/10 border border-[#D4AF37]/30 rounded-full mb-6 backdrop-blur-sm"
            variants={itemVariants}
            whileHover={{ scale: 1.05 }}
          >
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
            >
              <Sparkles className="w-4 h-4 text-[#D4AF37]" />
            </motion.div>
            <span className="text-xs sm:text-sm font-medium text-[#D4AF37]">{t('liveMarketData')}</span>
          </motion.div>

          {/* Main Headline */}
          <motion.h1 
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6"
            variants={itemVariants}
          >
            {t('heroTitle')}{' '}
            <span className="relative inline-block">
              <motion.span 
                className="text-gradient-gold"
                initial={{ backgroundPosition: "0% 50%" }}
                animate={{ backgroundPosition: ["0% 50%", "100% 50%", "0% 50%"] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                style={{
                  background: "linear-gradient(135deg, #D4AF37 0%, #F5ECD7 50%, #D4AF37 100%)",
                  backgroundSize: "200% 200%",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  backgroundClip: "text"
                }}
              >
                {t('heroHighlight')}
              </motion.span>
              <motion.span 
                className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-[#D4AF37] to-transparent rounded-full"
                initial={{ scaleX: 0, originX: 0 }}
                animate={{ scaleX: 1 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              />
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p 
            className="text-base sm:text-lg md:text-xl text-gray-400 leading-relaxed mb-8 md:mb-10 max-w-2xl"
            variants={itemVariants}
          >
            {t('heroSubtitle')}
          </motion.p>

          {/* CTA Buttons */}
          <motion.div 
            className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-10 md:mb-12"
            variants={itemVariants}
          >
            <motion.div
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={() => scrollToSection('calculator')}
                className="group btn-gold w-full sm:w-auto inline-flex items-center justify-center gap-2 text-black font-semibold px-6 sm:px-8 py-4 rounded-xl text-base"
              >
                {t('calculateWithGold')}
                <motion.div
                  animate={{ x: [0, 5, 0] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.div>
              </Button>
            </motion.div>
            
            <motion.div
              whileHover={{ scale: 1.03, y: -2 }}
              whileTap={{ scale: 0.98 }}
            >
              <Button
                onClick={() => scrollToSection('performance')}
                className="btn-outline-gold w-full sm:w-auto inline-flex items-center justify-center gap-2 text-[#D4AF37] font-semibold px-6 sm:px-8 py-4 rounded-xl text-base"
              >
                {t('viewPerformance')}
                <BarChart3 className="w-5 h-5" />
              </Button>
            </motion.div>
          </motion.div>

          {/* Trust Row */}
          <motion.div 
            className="flex flex-wrap gap-4 md:gap-6 lg:gap-10"
            variants={itemVariants}
          >
            {[
              { icon: Globe, text: t('publicMarketData') },
              { icon: Shield, text: t('transparentSources') },
              { icon: Info, text: t('notFinancialAdvice') }
            ].map((item, index) => (
              <motion.div 
                key={index} 
                className="flex items-center gap-2 text-gray-500 group"
                whileHover={{ x: isRTL ? -5 : 5 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                <motion.div
                  whileHover={{ rotate: 15, scale: 1.2 }}
                  transition={{ type: "spring", stiffness: 300 }}
                >
                  <item.icon className="w-4 h-4 sm:w-5 sm:h-5 text-[#D4AF37]/70 group-hover:text-[#D4AF37] transition-colors" />
                </motion.div>
                <span className="text-xs sm:text-sm font-medium group-hover:text-gray-300 transition-colors">{item.text}</span>
              </motion.div>
            ))}
          </motion.div>

          {/* Dashboard Promo Card - Only for non-authenticated users */}
          {!isAuthenticated && (
            <motion.div
              variants={itemVariants}
              className="mt-10 md:mt-12"
            >
              <motion.div
                whileHover={{ scale: 1.02, y: -3 }}
                className="relative bg-gradient-to-br from-[#1a1a24] to-[#12121a] rounded-2xl p-5 sm:p-6 overflow-hidden cursor-pointer group"
                onClick={() => navigate('/login')}
              >
                {/* Animated glowing border */}
                <motion.div
                  className="absolute inset-0 rounded-2xl"
                  style={{
                    background: 'linear-gradient(90deg, transparent, #D4AF37, #F5ECD7, #D4AF37, transparent)',
                    backgroundSize: '200% 100%',
                    padding: '2px',
                  }}
                  animate={{
                    backgroundPosition: ['0% 50%', '200% 50%'],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'linear',
                  }}
                >
                  <div className="absolute inset-[2px] rounded-2xl bg-gradient-to-br from-[#1a1a24] to-[#12121a]" />
                </motion.div>

                {/* Shimmer effect overlay */}
                <motion.div
                  className="absolute inset-0 rounded-2xl overflow-hidden"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                >
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-[#D4AF37]/20 to-transparent skew-x-12"
                    animate={{
                      x: ['-200%', '200%'],
                    }}
                    transition={{
                      duration: 3,
                      repeat: Infinity,
                      ease: 'easeInOut',
                      repeatDelay: 1,
                    }}
                  />
                </motion.div>

                {/* Pulsing background glows */}
                <motion.div 
                  className="absolute top-0 right-0 w-40 h-40 bg-[#D4AF37]/20 rounded-full blur-3xl"
                  animate={{
                    scale: [1, 1.3, 1],
                    opacity: [0.2, 0.4, 0.2],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />
                <motion.div 
                  className="absolute bottom-0 left-0 w-32 h-32 bg-[#D4AF37]/15 rounded-full blur-3xl"
                  animate={{
                    scale: [1.2, 1, 1.2],
                    opacity: [0.15, 0.3, 0.15],
                  }}
                  transition={{
                    duration: 2.5,
                    repeat: Infinity,
                    ease: 'easeInOut',
                    delay: 0.5,
                  }}
                />
                
                <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-4 sm:gap-6">
                  {/* Animated Icon */}
                  <motion.div 
                    className="flex-shrink-0 w-12 h-12 rounded-xl bg-gradient-to-br from-[#D4AF37] to-[#B8960C] flex items-center justify-center shadow-lg relative"
                    animate={{
                      boxShadow: [
                        '0 0 20px rgba(212, 175, 55, 0.3)',
                        '0 0 40px rgba(212, 175, 55, 0.6)',
                        '0 0 20px rgba(212, 175, 55, 0.3)',
                      ],
                    }}
                    transition={{
                      duration: 1.5,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                  >
                    <motion.div
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                    >
                      <Wallet className="w-6 h-6 text-black" />
                    </motion.div>
                  </motion.div>
                  
                  {/* Content */}
                  <div className="flex-grow">
                    <h3 className="text-lg sm:text-xl font-bold text-white mb-1 flex items-center gap-2">
                      {t('trackYourGold')}
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          rotate: [0, 15, -15, 0],
                        }}
                        transition={{
                          duration: 1.5,
                          repeat: Infinity,
                          ease: 'easeInOut',
                        }}
                      >
                        <Sparkles className="w-4 h-4 text-[#D4AF37]" />
                      </motion.div>
                    </h3>
                    <p className="text-sm text-gray-400 mb-3 sm:mb-0">
                      {t('promoDescription')}
                    </p>
                  </div>
                  
                  {/* Features badges with pulse - visible on larger screens */}
                  <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
                    <motion.div 
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37]/10 rounded-full border border-[#D4AF37]/30"
                      animate={{
                        borderColor: ['rgba(212, 175, 55, 0.3)', 'rgba(212, 175, 55, 0.6)', 'rgba(212, 175, 55, 0.3)'],
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <motion.div
                        animate={{ y: [0, -2, 0] }}
                        transition={{ duration: 1, repeat: Infinity }}
                      >
                        <TrendingUp className="w-3.5 h-3.5 text-[#D4AF37]" />
                      </motion.div>
                      <span className="text-xs text-[#D4AF37] font-medium">{t('dailyChanges')}</span>
                    </motion.div>
                    <motion.div 
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-[#D4AF37]/10 rounded-full border border-[#D4AF37]/30"
                      animate={{
                        borderColor: ['rgba(212, 175, 55, 0.3)', 'rgba(212, 175, 55, 0.6)', 'rgba(212, 175, 55, 0.3)'],
                      }}
                      transition={{ duration: 2, repeat: Infinity, delay: 0.5 }}
                    >
                      <ChartLine className="w-3.5 h-3.5 text-[#D4AF37]" />
                      <span className="text-xs text-[#D4AF37] font-medium">{t('performance')}</span>
                    </motion.div>
                  </div>
                  
                  {/* Animated CTA Button */}
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    animate={{
                      boxShadow: [
                        '0 0 0 0 rgba(212, 175, 55, 0)',
                        '0 0 0 8px rgba(212, 175, 55, 0.3)',
                        '0 0 0 0 rgba(212, 175, 55, 0)',
                      ],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      ease: 'easeInOut',
                    }}
                    className="flex items-center gap-2 bg-[#D4AF37] text-black px-5 py-2.5 rounded-xl text-sm font-semibold hover:bg-[#B8960C] transition-colors flex-shrink-0"
                  >
                    <span>{t('signIn')}</span>
                    <motion.div
                      animate={{ x: isRTL ? [-4, 0, -4] : [0, 4, 0] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    >
                      <ArrowRight className={`w-4 h-4 ${isRTL ? 'rotate-180' : ''}`} />
                    </motion.div>
                  </motion.button>
                </div>

                {/* Animated bottom accent line */}
                <motion.div 
                  className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent"
                  animate={{
                    opacity: [0.3, 0.8, 0.3],
                    scaleX: [0.8, 1, 0.8],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    ease: 'easeInOut',
                  }}
                />

                {/* Sparkle particles */}
                {[...Array(5)].map((_, i) => (
                  <motion.div
                    key={i}
                    className="absolute w-1 h-1 bg-[#D4AF37] rounded-full"
                    style={{
                      left: `${20 + i * 15}%`,
                      top: `${30 + (i % 2) * 40}%`,
                    }}
                    animate={{
                      opacity: [0, 1, 0],
                      scale: [0, 1.5, 0],
                    }}
                    transition={{
                      duration: 2,
                      repeat: Infinity,
                      delay: i * 0.4,
                      ease: 'easeInOut',
                    }}
                  />
                ))}
              </motion.div>
            </motion.div>
          )}
        </motion.div>

        {/* Decorative Gold Bar - Desktop only */}
        <motion.div 
          className="absolute right-0 top-1/2 -translate-y-1/2 hidden lg:block"
          variants={floatingVariants}
          initial="initial"
          animate="animate"
        >
          <motion.div 
            className="w-2 h-64 bg-gradient-to-b from-transparent via-[#D4AF37]/30 to-transparent rounded-full"
            animate={{
              opacity: [0.3, 0.6, 0.3],
            }}
            transition={{
              duration: 3,
              repeat: Infinity,
              ease: "easeInOut"
            }}
          />
        </motion.div>

        {/* Floating gold coins decoration - Desktop */}
        <div className="absolute right-10 top-1/3 hidden xl:block">
          {[0, 1, 2].map((i) => (
            <motion.div
              key={i}
              className="absolute w-8 h-8 rounded-full bg-gradient-to-br from-[#F5ECD7] to-[#B8960C] shadow-lg"
              style={{
                right: i * 30,
                top: i * 40,
              }}
              initial={{ opacity: 0, scale: 0 }}
              animate={{ 
                opacity: [0.4, 0.8, 0.4],
                scale: 1,
                y: [0, -10, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                ease: "easeInOut",
                delay: i * 0.5 + 1
              }}
            >
              <div className="absolute inset-0 rounded-full bg-gradient-to-tr from-transparent via-white/30 to-transparent" />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
